import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BillItem, IClientFloors, IClientPlaces, ITables, UserType } from '../../../all-types'
import {
  addTableItem,
  getFloorList,
  getPlaceList,
  getTableById,
  getTableList,
  getUserList,
  lockTable,
  removeTableItem,
  unlockTable,
  updateCartItemOfTheTable,
  updateTableItem,
  updateTableItemStatus,
} from '../../../services'

// Server stamps `kitchInfo.updatedAt` on every item status transition.
// We use that as a per-item monotonic version so that out-of-order or stale
// snapshots (HTTP response from PUT-1 arriving after a `tables:list` socket
// emit triggered by PUT-3, etc.) cannot revert an item that has already
// advanced. This was the root cause of the "Start Cooking flicker": three
// rapid PUTs each emit a snapshot, and a late-arriving snapshot from PUT-1
// would visually push items 2/3 back to the previous column for an instant.
const itemTime = (i?: BillItem | null): number => {
  if (!i?.kitchInfo?.updatedAt) return 0
  const t = new Date(i.kitchInfo.updatedAt as unknown as string).getTime()
  return Number.isFinite(t) ? t : 0
}

const mergeItemsByUpdatedAt = (
  current: BillItem[] | undefined,
  incoming: BillItem[] | undefined
): BillItem[] | undefined => {
  if (!incoming) return incoming
  if (!current?.length) return incoming
  const bySno = new Map<number, BillItem>()
  for (const it of current) bySno.set(it.sno, it)
  return incoming.map((inc) => {
    const cur = bySno.get(inc.sno)
    if (!cur) return inc
    return itemTime(cur) > itemTime(inc) ? cur : inc
  })
}

const mergeTableByUpdatedAt = (
  current: ITables | undefined,
  incoming: ITables
): ITables => {
  if (!current?.billSession?.items?.length) return incoming
  if (!incoming?.billSession?.items?.length) return incoming
  const merged = mergeItemsByUpdatedAt(
    current.billSession.items,
    incoming.billSession.items
  )
  return {
    ...incoming,
    billSession: { ...incoming.billSession, items: merged ?? [] },
  }
}

const replaceOrInsertTable = (
  tables: ITables[],
  incoming: ITables
): ITables[] => {
  if (!incoming?._id) return tables
  let found = false
  const next = tables.map((t) => {
    if (t._id !== incoming._id) return t
    found = true
    return mergeTableByUpdatedAt(t, incoming)
  })
  return found ? next : [...next, incoming]
}

const initialState: {
  floors: IClientFloors[]
  places: IClientPlaces[]
  tables: ITables[]
  users: UserType[]
  selectedFloor: IClientFloors | null
} = {
  floors: [],
  places: [],
  tables: [],
  users: [],
  selectedFloor: null,
}

export const PreloadDataSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setFloors: (state, action: PayloadAction<IClientFloors[]>) => {
      state.floors = action.payload
    },
    setSelectedFloor: (state, action) => {
      state.selectedFloor = action.payload
    },
    updateAllTables: (state, action: PayloadAction<ITables[]>) => {
      const incoming = action.payload || []
      // Smart-merge against current state so per-item updatedAt versioning
      // protects items that have been advanced from being reverted by a
      // late-arriving stale snapshot (see comment at the top of this file).
      const byId = new Map<string, ITables>()
      for (const t of state.tables) byId.set(t._id, t)
      state.tables = incoming.map((inc) => mergeTableByUpdatedAt(byId.get(inc._id), inc))
    },
    updateTable: (state, action: PayloadAction<ITables>) => {
      const updatedTable = action.payload
      if (updatedTable?._id) {
        state.tables = replaceOrInsertTable(state.tables, updatedTable)
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFloorList.fulfilled, (state, action) => {
        state.floors = action.payload
      })
      .addCase(getPlaceList.fulfilled, (state, action) => {
        state.places = action.payload
      })
      // Full reload from the server — explicit user refresh, so trust it.
      .addCase(getTableList.fulfilled, (state, action) => {
        state.tables = action.payload
      })
      .addCase(getTableById.fulfilled, (state, action) => {
        if (action.payload?._id) {
          state.tables = replaceOrInsertTable(state.tables, action.payload)
        }
      })
      .addCase(updateCartItemOfTheTable.fulfilled, (state, action) => {
        if (!action.payload?._id) return
        state.tables = replaceOrInsertTable(state.tables, action.payload)
      })
      .addCase(updateTableItemStatus.fulfilled, (state, action) => {
        if (action.payload?._id) {
          state.tables = replaceOrInsertTable(state.tables, action.payload)
        }
      })
      .addCase(getUserList.fulfilled, (state, action) => {
        state.users = action.payload
      })
      .addCase(lockTable.fulfilled, (state, action) => {
        if (action.payload?._id) {
          state.tables = replaceOrInsertTable(state.tables, action.payload)
        }
      })
      .addCase(unlockTable.fulfilled, (state, action) => {
        if (action.payload?._id) {
          state.tables = replaceOrInsertTable(state.tables, action.payload)
        }
      })
      // Granular single-item mutations reflect into the table list the same
      // way as the whole-table PUT (skip null, merge by updatedAt).
      // (addMatcher must come after every addCase.)
      .addMatcher(
        (action) =>
          [
            addTableItem.fulfilled.type,
            updateTableItem.fulfilled.type,
            removeTableItem.fulfilled.type,
          ].includes(action.type),
        (state, action: PayloadAction<ITables | null>) => {
          if (!action.payload?._id) return
          state.tables = replaceOrInsertTable(state.tables, action.payload)
        }
      )
  },
})

export const { setFloors, setSelectedFloor, updateTable, updateAllTables } = PreloadDataSlice.actions
